import React, { useState } from 'react';
import { TrendingDate } from '../../types';

interface TrendingDateSelectorProps {
  dates: TrendingDate[];
  selectedDate: string;
  onDateChange: (date: string) => void;
  loading: boolean;
}

const TrendingDateSelector: React.FC<TrendingDateSelectorProps> = ({
  dates,
  selectedDate,
  onDateChange,
  loading
}) => {
  const [showAllDates, setShowAllDates] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // 控制整个组件的展开/折叠

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateStr === today.toISOString().split('T')[0]) {
      return '今天';
    } else if (dateStr === yesterday.toISOString().split('T')[0]) {
      return '昨天';
    } else {
      return date.toLocaleDateString('zh-CN', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const getDateStatus = (date: TrendingDate): string => {
    if (date.ai_generated) {
      return 'complete';
    } else if (date.total_projects > 0) {
      return 'partial';
    } else {
      return 'empty';
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case 'complete':
        return 'fas fa-check-circle';
      case 'partial':
        return 'fas fa-exclamation-circle';
      default:
        return 'fas fa-circle';
    }
  };

  const getStatusText = (date: TrendingDate): string => {
    if (date.ai_generated) {
      return `${date.total_projects} 个项目，${date.projects_with_ai} 个AI解读`;
    } else if (date.total_projects > 0) {
      return `${date.total_projects} 个项目，无AI解读`;
    } else {
      return '暂无数据';
    }
  };

  // 显示的日期列表（默认显示最近7天）
  const displayDates = showAllDates ? dates : dates.slice(0, 7);

  return (
    <div className={`trending-date-selector ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* 紧凑的头部 - 始终显示 */}
      <div className="date-selector-compact-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="compact-header-left">
          <i className="fas fa-calendar-alt"></i>
          <span className="compact-title">日期选择</span>
          <span className="current-date-badge">{formatDate(selectedDate)}</span>
          {(() => {
            const currentDate = dates.find(d => d.date === selectedDate);
            if (currentDate && !isExpanded) {
              return (
                <span className="compact-stats">
                  {currentDate.total_projects}个项目
                  {currentDate.ai_generated && (
                    <i className="fas fa-check-circle ai-complete-icon" title="已完成AI解读"></i>
                  )}
                </span>
              );
            }
            return null;
          })()}
        </div>
        <div className="compact-header-right">
          <button className="expand-toggle-btn">
            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
            <span>{isExpanded ? '收起' : '展开'}</span>
          </button>
        </div>
      </div>

      {/* 可折叠的详细内容 */}
      {isExpanded && (
        <div className="date-selector-expanded-content">
          <div className="date-selector-header">
            <h3>
              <i className="fas fa-calendar-alt"></i>
              选择日期
            </h3>
            {dates.length > 7 && (
              <button
                className="toggle-dates-btn"
                onClick={() => setShowAllDates(!showAllDates)}
              >
                {showAllDates ? (
                  <>
                    <i className="fas fa-chevron-up"></i>
                    收起
                  </>
                ) : (
                  <>
                    <i className="fas fa-chevron-down"></i>
                    查看更多
                  </>
                )}
              </button>
            )}
          </div>

          <div className="date-selector-content">
            {loading ? (
              <div className="date-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <span>加载中...</span>
              </div>
            ) : displayDates.length === 0 ? (
              <div className="date-empty">
                <i className="fas fa-calendar-times"></i>
                <span>暂无可用日期</span>
              </div>
            ) : (
              <div className="dates-list">
                {displayDates.map((date) => {
                  const status = getDateStatus(date);
                  const isSelected = date.date === selectedDate;

                  return (
                    <div
                      key={date.date}
                      className={`date-item ${status} ${isSelected ? 'selected' : ''}`}
                      onClick={() => onDateChange(date.date)}
                    >
                      <div className="date-main">
                        <div className="date-label">
                          <span className="date-text">{formatDate(date.date)}</span>
                          <span className="date-full">{date.date}</span>
                        </div>

                        <div className="date-status">
                          <i className={getStatusIcon(status)}></i>
                        </div>
                      </div>

                      <div className="date-info">
                        <span className="date-stats">{getStatusText(date)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 当前选中日期的详细信息 */}
          {selectedDate && (
            <div className="selected-date-info">
              <div className="selected-date-header">
                <i className="fas fa-calendar-check"></i>
                <span>当前选择：{formatDate(selectedDate)}</span>
                <span className="date-badge">{selectedDate}</span>
              </div>

              {(() => {
                const currentDate = dates.find(d => d.date === selectedDate);
                if (currentDate) {
                  return (
                    <div className="selected-date-details">
                      <div className="detail-item">
                        <i className="fas fa-star"></i>
                        <span>{currentDate.total_projects} 个项目</span>
                      </div>
                      <div className="detail-item">
                        <i className="fas fa-brain"></i>
                        <span>{currentDate.projects_with_ai} 个AI解读</span>
                      </div>
                      {currentDate.ai_generated && (
                        <div className="detail-item status-complete">
                          <i className="fas fa-check-circle"></i>
                          <span>已完成AI解读</span>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrendingDateSelector;
